import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, RotateCcw } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';

interface AuditLogFilterPanelProps {
  filters: {
    warehouseId?: number;
    actionType?: string;
    dateFrom?: string;
    dateTo?: string;
  };
  onFilterChange: (filters: any) => void;
  onReset: () => void;
}

const AuditLogFilterPanel: React.FC<AuditLogFilterPanelProps> = ({
  filters,
  onFilterChange,
  onReset,
}) => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [actionTypes, setActionTypes] = useState<string[]>([]);

  useEffect(() => {
    loadWarehouses();
    loadActionTypes();
  }, []);

  const loadWarehouses = async () => {
    try {
      const data = await warehouseAPI.getAll({ status: 'ACTIVE' });
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses', error);
    }
  };

  const loadActionTypes = async () => {
    try {
      const data = await warehouseAPI.getAuditActionTypes();
      setActionTypes(data);
    } catch (error) {
      console.error('Failed to load action types', error);
    }
  };

  const handleFilterUpdate = (key: string, value: any) => {
    onFilterChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const getActionTypeLabel = (type: string): string => {
    return t(`warehouse.auditLog.actionTypes.${type}`, { defaultValue: type });
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-5 h-5 text-gray-500" />
        <h3 className="text-lg font-semibold">{t('warehouse.auditLog.filter')}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Warehouse Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.auditLog.warehouse')}</label>
          <select
            value={filters.warehouseId || ''}
            onChange={(e) =>
              handleFilterUpdate('warehouseId', e.target.value ? parseInt(e.target.value) : null)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('warehouse.auditLog.allWarehouses')}</option>
            {warehouses.map((wh) => (
              <option key={wh.id} value={wh.id}>
                {wh.name} ({wh.code})
              </option>
            ))}
          </select>
        </div>

        {/* Action Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.auditLog.actionType')}</label>
          <select
            value={filters.actionType || ''}
            onChange={(e) => handleFilterUpdate('actionType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('warehouse.auditLog.allActions')}</option>
            {actionTypes.map((type) => (
              <option key={type} value={type}>
                {getActionTypeLabel(type)}
              </option>
            ))}
          </select>
        </div>

        {/* Date From Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.auditLog.fromDate')}</label>
          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => handleFilterUpdate('dateFrom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Date To Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.auditLog.toDate')}</label>
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => handleFilterUpdate('dateTo', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Reset Button */}
      <div className="mt-4 flex justify-end">
        <button
          onClick={onReset}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          {t('warehouse.auditLog.reset')}
        </button>
      </div>
    </div>
  );
};

export default AuditLogFilterPanel;
